// ============ TWEAKS PANEL ============
// Production adaptation of the prototype's tweaks scaffold: the in-iframe
// design-host protocol (postMessage / EDITMODE block) is removed. Values
// persist to localStorage and the panel is opened by a small floating button,
// so end users can adjust live visual settings (theme, motion, font scale).

import React, { useState, useEffect, useCallback } from 'react';

const STORE_KEY = 'mm_tweaks';

export function useTweaks<T extends Record<string, any>>(defaults: T): [T, (k: keyof T | Partial<T>, v?: any) => void] {
  const [values, setValues] = useState<T>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      return saved ? { ...defaults, ...saved } : defaults;
    } catch { return defaults; }
  });
  useEffect(() => { localStorage.setItem(STORE_KEY, JSON.stringify(values)); }, [values]);
  const setTweak = useCallback((keyOrEdits: any, val?: any) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null ? keyOrEdits : { [keyOrEdits]: val };
    setValues(prev => ({ ...prev, ...edits }));
  }, []);
  return [values, setTweak];
}

const STYLE = `
  .twk-fab{position:fixed;right:16px;bottom:16px;z-index:2147483645;width:42px;height:42px;
    border-radius:50%;border:1px solid rgba(139,92,246,.4);background:rgba(13,20,32,.82);
    color:#e8ecf4;cursor:pointer;display:flex;align-items:center;justify-content:center;
    -webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);
    box-shadow:0 8px 28px rgba(2,5,12,.5);transition:transform .15s,box-shadow .15s}
  .twk-fab:hover{transform:translateY(-2px);box-shadow:0 12px 32px rgba(139,92,246,.35)}
  /* Below 900px the sidebar is replaced by a fixed bottom bar, and this button
     sat inside that band with z-index 2147483645 - so it covered whichever nav
     item happened to be scrolled under it, and that item could not be tapped
     at all. Lift both above the bar.
     The bar is 65px tall and sits ON the bottom edge, carrying the home
     indicator in its own padding - so these offsets must add the same inset,
     or a phone with a gesture bar pushes the bar back up under this button. */
  @media (max-width:900px){
    .twk-fab{bottom:calc(77px + env(safe-area-inset-bottom, 0px))}
    .twk-panel{bottom:calc(129px + env(safe-area-inset-bottom, 0px));
      max-height:calc(100vh - 160px - env(safe-area-inset-bottom, 0px))}
  }
  .twk-panel{position:fixed;right:16px;bottom:70px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 100px);display:flex;flex-direction:column;
    background:rgba(13,20,32,.9);color:#e8ecf4;
    -webkit-backdrop-filter:blur(22px) saturate(150%);backdrop-filter:blur(22px) saturate(150%);
    border:1px solid rgba(139,92,246,.22);border-radius:14px;
    box-shadow:0 18px 50px rgba(2,5,12,.6);
    font:12px/1.4 "IBM Plex Sans Thai",ui-sans-serif,system-ui,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 10px 12px 16px}
  .twk-hd b{font-size:13px;font-weight:600}
  .twk-x{border:0;background:transparent;color:rgba(232,236,244,.5);width:24px;height:24px;
    border-radius:6px;cursor:pointer;font-size:14px}
  .twk-x:hover{background:rgba(255,255,255,.08);color:#fff}
  .twk-body{padding:2px 16px 16px;display:flex;flex-direction:column;gap:11px;overflow-y:auto}
  .twk-row{display:flex;flex-direction:column;gap:6px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;color:rgba(232,236,244,.78)}
  .twk-val{color:rgba(232,236,244,.5);font-family:"IBM Plex Mono","IBM Plex Sans Thai",monospace}
  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;
    color:rgba(139,92,246,.85);padding:8px 0 0}
  .twk-sect:first-child{padding-top:0}
  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:4px 0;
    border-radius:999px;background:rgba(255,255,255,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;
    background:#8b5cf6;cursor:pointer;box-shadow:0 0 8px rgba(139,92,246,.6)}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;border:0;background:#8b5cf6;cursor:pointer}
  .twk-toggle{position:relative;width:34px;height:19px;border:0;border-radius:999px;
    background:rgba(255,255,255,.15);transition:background .15s;cursor:pointer;padding:0}
  .twk-toggle[data-on="1"]{background:#00e0b0}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:15px;height:15px;border-radius:50%;
    background:#fff;transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(15px)}
  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;flex:1;min-width:0;height:42px;border:1px solid rgba(255,255,255,.12);
    border-radius:7px;overflow:hidden;cursor:pointer;display:flex}
  .twk-chip[data-on="1"]{border-color:#fff;box-shadow:0 0 0 1px #fff}
  .twk-chip i{flex:1}
`;

export function TweakSection({ label }: { label: string }) {
  return <div className="twk-sect">{label}</div>;
}

export function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }: any) {
  return (
    <div className="twk-row">
      <div className="twk-lbl"><span>{label}</span><span className="twk-val">{value}{unit}</span></div>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
        value={value} onChange={e => onChange(Number(e.target.value))} />
    </div>
  );
}

export function TweakToggle({ label, value, onChange }: any) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
        role="switch" aria-checked={!!value} onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

export function TweakColor({ label, value, options, onChange }: any) {
  const key = (o: any) => JSON.stringify(o);
  const cur = key(value);
  return (
    <div className="twk-row">
      <div className="twk-lbl"><span>{label}</span></div>
      <div className="twk-chips">
        {options.map((o: any, i: number) => {
          const colors = Array.isArray(o) ? o : [o];
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" data-on={on ? '1' : '0'}
              title={colors.join(' · ')} onClick={() => onChange(o)}>
              {colors.map((c: string, j: number) => <i key={j} style={{ background: c }} />)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TweaksPanel({ title = 'Tweaks', children }: any) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <style>{STYLE}</style>
      <button className="twk-fab" title="ปรับแต่งธีม" onClick={() => setOpen(o => !o)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"></path>
        </svg>
      </button>
      {open && (
        <div className="twk-panel">
          <div className="twk-hd"><b>{title}</b><button className="twk-x" onClick={() => setOpen(false)}>✕</button></div>
          <div className="twk-body">{children}</div>
        </div>
      )}
    </>
  );
}
